/**
 * FancySelect: branded, animated single-choice picker.
 *
 * Built without Radix so it ships before the rest of the shadcn primitives are
 * uncommented. Goals:
 *   - Replaces the native <select> for a more on-brand feel (matches our
 *     rounded inputs, design tokens, and animated dropdown).
 *   - Keyboard accessible: Up/Down to move, Enter / Space to select, Esc to
 *     close, type-ahead jumps to first option starting with that letter.
 *   - Closes on outside click, scroll, or Escape.
 *   - Uses the existing `slide-up-fade` keyframe from the Tailwind preset.
 *
 * The input is fully controlled: the parent owns `value`.
 */
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../lib/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FancySelectOption<TValue extends string = string> {
  value:    TValue;
  label:    React.ReactNode;
  /** Short hint shown to the right of the label (e.g. "Recommended"). */
  hint?:    React.ReactNode;
  disabled?: boolean;
}

export interface FancySelectProps<TValue extends string = string> {
  options:  ReadonlyArray<FancySelectOption<TValue>>;
  value:    TValue | '';
  onChange: (v: TValue) => void;
  placeholder?: string;
  /** Marks the trigger as invalid (red border). */
  invalid?: boolean;
  disabled?: boolean;
  id?:      string;
  ariaLabel?:      string;
  ariaLabelledBy?: string;
  className?: string;
  /** Optional leading icon shown inside the trigger. */
  leadingIcon?: React.ElementType;
  /** Turn the trigger into a filter box while open. Worth it for long lists. */
  searchable?: boolean;
}

/** Fixed coords for the portaled listbox (top OR bottom, never both). */
interface ListPos {
  left: number;
  width: number;
  top?: number;
  bottom?: number;
  maxHeight: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FancySelect<TValue extends string = string>({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  invalid     = false,
  disabled    = false,
  id,
  ariaLabel,
  ariaLabelledBy,
  className,
  leadingIcon: LeadingIcon,
  searchable = false,
}: FancySelectProps<TValue>) {
  const [open,        setOpen]        = React.useState(false);
  const [activeIdx,   setActiveIdx]   = React.useState<number>(() =>
    Math.max(0, options.findIndex((o) => o.value === value)),
  );
  /** Fixed-position box for the portaled list, measured from the trigger. */
  const [pos, setPos] = React.useState<ListPos | null>(null);
  const listMounted = pos !== null;
  const [query, setQuery] = React.useState('');

  const anchorRef  = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const inputRef   = React.useRef<HTMLInputElement | null>(null);
  const listRef    = React.useRef<HTMLUListElement | null>(null);

  // Options actually shown (and indexed by activeIdx / commit).
  const shown = React.useMemo(() => {
    const q = searchable ? query.trim().toLowerCase() : '';
    if (!q) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query, searchable]);

  /** Place the fixed-position list against the trigger, flipping up if needed. */
  const measure = React.useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gap = 6;
    const below = window.innerHeight - rect.bottom - gap;
    const above = rect.top - gap;
    const up = below < 200 && above > below;
    setPos({
      left: rect.left,
      width: rect.width,
      ...(up ? { bottom: window.innerHeight - rect.top + gap } : { top: rect.bottom + gap }),
      maxHeight: Math.max(140, Math.min(240, up ? above : below)),
    });
  }, []);

  // Keep the highlighted option visible. Only the list's own scrollTop is
  // touched, so this can never scroll an ancestor (a Dialog) out from under us.
  React.useEffect(() => {
    if (!open) return;
    const list = listRef.current;
    const el = list?.children[activeIdx] as HTMLElement | undefined;
    if (!list || !el) return;
    const top = el.offsetTop;
    const bottom = top + el.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [open, activeIdx, listMounted]);

  // A modal Dialog's scroll lock (react-remove-scroll) listens for wheel on
  // document and preventDefaults anything outside the dialog, which would stop
  // this body-portaled list from scrolling. Its listener is bubble-phase, so
  // stopping the event at the list keeps native scrolling working.
  React.useEffect(() => {
    const el = listRef.current;
    if (!open || !el) return;
    const stop = (e: Event) => e.stopPropagation();
    el.addEventListener('wheel', stop, { passive: false });
    el.addEventListener('touchmove', stop, { passive: false });
    return () => {
      el.removeEventListener('wheel', stop);
      el.removeEventListener('touchmove', stop);
    };
  }, [open, listMounted]);

  // Close on outside click; follow the trigger on scroll/resize.
  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (
        anchorRef.current && !anchorRef.current.contains(t) &&
        listRef.current   && !listRef.current.contains(t)
      ) {
        setOpen(false);
      }
    }
    function onReposition(e: Event) {
      // Scrolling the list itself must not move it. Any other scroll (e.g. the
      // surrounding Dialog) just re-anchors the list. Closing here would make
      // the list feel unscrollable, since a wheel over it chains to the dialog.
      const t = e.target as Node | null;
      if (t && listRef.current && listRef.current.contains(t)) return;
      measure();
    }
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [open, measure]);

  // Reset the active index and measure where to place the list. Focus stays on
  // the trigger: the list is portaled to <body>, so focusing it would fight a
  // Dialog's focus trap.
  React.useEffect(() => {
    if (!open) {
      setPos(null);
      setQuery('');
      return;
    }
    setActiveIdx(Math.max(0, options.findIndex((o) => o.value === value)));
    // The filter box lives in the trigger (inside any Dialog), so focusing it
    // doesn't fight the dialog's focus trap the way the portaled list would.
    if (searchable) {
      requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    }

    measure();
  }, [open, options, value, searchable, measure]);

  function commit(idx: number) {
    const opt = shown[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function nextEnabledIdx(start: number, dir: 1 | -1): number {
    const len = shown.length;
    if (len === 0) return start;
    let i = start;
    for (let n = 0; n < len; n++) {
      i = (i + dir + len) % len;
      if (!shown[i]?.disabled) return i;
    }
    return start;
  }

  // All keyboard handling lives on the trigger, which keeps focus while the
  // portaled list is open.
  function handleTriggerKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape')         { e.preventDefault(); setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => nextEnabledIdx(i,  1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => nextEnabledIdx(i, -1)); }
    else if (e.key === 'Enter')     { e.preventDefault(); commit(activeIdx); }
    // Home/End and type-ahead only when there's no filter box to type into.
    else if (!searchable && e.key === 'Home') { e.preventDefault(); setActiveIdx(nextEnabledIdx(-1, 1)); }
    else if (!searchable && e.key === 'End')  { e.preventDefault(); setActiveIdx(nextEnabledIdx(shown.length, -1)); }
    else if (!searchable && e.key === ' ')    { e.preventDefault(); commit(activeIdx); }
    else if (!searchable && e.key.length === 1 && e.key.match(/\S/)) {
      const ch  = e.key.toLowerCase();
      const idx = shown.findIndex(
        (o, i) => i > activeIdx && !o.disabled && String(o.label).toLowerCase().startsWith(ch),
      );
      if (idx >= 0) setActiveIdx(idx);
      else {
        const wrap = shown.findIndex((o) => !o.disabled && String(o.label).toLowerCase().startsWith(ch));
        if (wrap >= 0) setActiveIdx(wrap);
      }
    }
  }

  const selected = options.find((o) => o.value === value);

  const boxClass = cn(
    'w-full inline-flex items-center justify-between gap-2 rounded-lg border bg-background',
    'pl-3 pr-2.5 py-2.5 text-sm text-foreground transition-all',
    'focus-within:ring-2 focus-within:ring-ring',
    invalid ? 'border-destructive' : 'border-border',
  );

  return (
    <div ref={anchorRef} className={cn('relative', className)}>
      {searchable && open ? (
        <div className={boxClass}>
          {LeadingIcon && (
            <LeadingIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
          )}
          <input
            ref={inputRef}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded
            aria-controls={`${id ?? 'fancy-select'}-listbox`}
            aria-activedescendant={`fancy-select-opt-${activeIdx}`}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={handleTriggerKeyDown}
            placeholder={selected ? String(selected.label) : placeholder}
            className="min-w-0 flex-1 bg-transparent text-left outline-none placeholder:text-muted-foreground"
          />
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 rotate-180" aria-hidden />
        </div>
      ) : (
      <button
        ref={triggerRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id ?? 'fancy-select'}-listbox` : undefined}
        aria-activedescendant={open ? `fancy-select-opt-${activeIdx}` : undefined}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          'w-full inline-flex items-center justify-between gap-2 rounded-lg border bg-background',
          'pl-3 pr-2.5 py-2.5 text-sm text-foreground transition-all',
          'hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          invalid ? 'border-destructive' : 'border-border',
        )}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {LeadingIcon && (
            <LeadingIcon className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
          )}
          <span className={cn('truncate text-left', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
        </span>
        <ChevronDown
          className={cn('h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      )}

      {open && pos && createPortal(
        <ul
          ref={listRef}
          id={`${id ?? 'fancy-select'}-listbox`}
          role="listbox"
          // A modal Radix Dialog sets pointer-events:none on <body> and
          // dismisses on outside pointerdown, so keep this layer clickable and
          // stop the event before it reaches the dialog's document listener.
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            left: pos.left,
            width: pos.width,
            top: pos.top,
            bottom: pos.bottom,
            maxHeight: pos.maxHeight,
          }}
          className={cn(
            // Portaled to <body> above the Dialog (z-50) so an overflow-y-auto
            // modal can't clip it.
            // overscroll-contain keeps a wheel at the list's end from scrolling
            // the dialog behind it.
            'z-[60] overflow-y-auto overscroll-contain pointer-events-auto',
            'rounded-xl border border-border bg-background shadow-lg',
            'animate-slide-up-fade origin-top focus:outline-none',
            'p-1',
          )}
        >
          {shown.length === 0 && (
            <li className="px-2.5 py-3 text-center text-xs text-muted-foreground">No matches</li>
          )}
          {shown.map((opt, idx) => {
            const active   = idx === activeIdx;
            const selected = opt.value === value;
            return (
              <li
                key={opt.value}
                id={`fancy-select-opt-${idx}`}
                role="option"
                aria-selected={selected}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => !opt.disabled && setActiveIdx(idx)}
                // preventDefault stops label activation: when this select sits
                // inside a <label>, the click would otherwise be forwarded to
                // the trigger button and reopen the list right after selecting.
                onClick={(e) => {
                  e.preventDefault();
                  commit(idx);
                }}
                className={cn(
                  'flex items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-sm cursor-pointer',
                  'transition-colors',
                  active && !opt.disabled ? 'bg-secondary text-secondary-foreground' : 'text-foreground',
                  opt.disabled && 'opacity-40 cursor-not-allowed',
                )}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Check
                    className={cn(
                      'h-3.5 w-3.5 text-foreground shrink-0 transition-opacity',
                      selected ? 'opacity-100' : 'opacity-0',
                    )}
                    style={selected ? { color: 'hsl(var(--palette-forest))' } : undefined}
                    aria-hidden
                  />
                  <span className="truncate">{opt.label}</span>
                </span>
                {opt.hint && (
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">
                    {opt.hint}
                  </span>
                )}
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </div>
  );
}
